import { Button, Card, List, notification, Spin, Tooltip } from 'antd';
import { OverViewCardInfo } from '../OverviewCardInfo';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import { DeleteOutline, EditOutlined, DifferenceOutlined } from '@mui/icons-material';
import './PhoneWorkflowInfo.scss';
import { useEffect, useState } from 'react';
import { CustomModal } from '@/components/CustomModal';
import PhoneWorkflowForm from './PhoneWorkflowForm';
import { RefetchOptions, QueryObserverResult, UseMutationResult } from '@tanstack/react-query';
import { MODAL_IDS } from 'src/enums/EModal';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal, closeModal } from '@/store/modal/slice';
import { deepEqual, safeJSONParse } from '@/utils/helper';
import { User } from '@/types/TAccounts';
import { TUTORIALS } from 'src/enums/ETutorials';
import { TWorkflowAvailabilities } from '@/types/TWorkflowAvailabilities';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { fetchAndUpdateWorkflowAnalysis } from '@/utils/analysisWorkflow';
import { getSelectedAgentId } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';

import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal/ConfirmDeleteModal';

import { decrementScenarioCount } from '@/store/scenarioCounts/slice';
import { FormActionType, WorkflowType } from 'src/enums/EWorkflow';
import { InfoCircleOutlined, LoadingOutlined, SettingOutlined } from '@ant-design/icons';
import { SetupWorkflowAvailability } from '../../../../../components/WorkflowAvailability/WorkflowAvailability';

interface PhoneWorkflowInfoProps {
	data: any;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	debrand?: boolean;
}

export const PhoneWorkflowInfo: React.FC<PhoneWorkflowInfoProps> = ({
	data,
	refetch,
	mutation,
	debrand,
}) => {
	const dispatch = useAppDispatch();
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const canAddScenario = useAppSelector(selectCanAddScenario);
	const [intakeWorkflow, setIntakeWorkflow] = useState<any>(null);

	const beginningCallWorkflowQuestions: string[] = safeJSONParse(data?.intake_questions, []);
	const phoneWorkflowdata = safeJSONParse(data?.intake_questions_workflows, []);

	const [formInitialValues, setFormInitialValues] = useState<any>(null);
	const [scenarioIndex, setScenarioIndex] = useState<number>();
	const [duplicateWorkflow, setDuplicateWorkflow] = useState<any>(null);

	const isWorkflowAvailabilityFeatureEnabled = useFeatureFlag(FeatureFlag.WORKFLOW_AVAILABILITY);
	useEffect(() => {
		let previousWorkflowData = [];

		if (beginningCallWorkflowQuestions && beginningCallWorkflowQuestions.length > 0) {
			previousWorkflowData.push({
				customScenario: '',
				description: 'beginning',
				questions: beginningCallWorkflowQuestions,
				boc_intake_post_execution_prompt: data?.boc_intake_post_execution_prompt || '',
			});
		}

		previousWorkflowData = [...previousWorkflowData, ...phoneWorkflowdata];

		if (!deepEqual(previousWorkflowData, formInitialValues?.scenarios[0]?.previousWorkflowData)) {
			const initialValues = {
				scenarios: [
					{
						previousWorkflowData,
					},
				],
			};

			setFormInitialValues(initialValues);
		}
	}, [phoneWorkflowdata, beginningCallWorkflowQuestions, formInitialValues]);

	const handleEditScenario = (scenario: any, index: number) => {
		let previousWorkflowData = [];

		if (beginningCallWorkflowQuestions && beginningCallWorkflowQuestions.length > 0) {
			previousWorkflowData.push({
				customScenario: '',
				description: 'beginning',
				questions: beginningCallWorkflowQuestions,
				boc_intake_post_execution_prompt: data?.boc_intake_post_execution_prompt || '',
			});
		}

		previousWorkflowData = [...previousWorkflowData, ...phoneWorkflowdata];

		const initialValues = {
			scenarios: [
				{
					customScenario:
						scenario.customScenario?.toLowerCase() === 'beginning of call'
							? ''
							: scenario.customScenario,
					description:
						scenario.customScenario?.toLowerCase() === 'beginning of call'
							? 'beginning'
							: 'custom-scenario',
					questions: scenario.questions?.map((q: string) => ({ question: q })),
					final_collection_phrase: scenario.final_collection_phrase,
					index,

					previousWorkflowData,
					postExecutionPrompt: scenario?.postExecutionPrompt,
					availability: scenario.availability || null,
					configEnabled: scenario.configEnabled ?? false,
					fallbackMessage: scenario.fallbackMessage || '',
					boc_intake_post_execution_prompt:
						scenario.boc_intake_post_execution_prompt ||
						data?.boc_intake_post_execution_prompt ||
						'',
				},
			],
		};
		setFormInitialValues(initialValues);
		setScenarioIndex(index);
		dispatch(openModal({ modalId: MODAL_IDS.PHONE_WORKFLOW_EDIT_MODAL }));
	};

	const handleDuplicateScenario = (index: number) => {
		let previousWorkflowData = [];

		if (beginningCallWorkflowQuestions && beginningCallWorkflowQuestions.length > 0) {
			previousWorkflowData.push({
				customScenario: '',
				description: 'beginning',
				questions: beginningCallWorkflowQuestions,
			});
		}

		previousWorkflowData = [...previousWorkflowData, ...phoneWorkflowdata];

		const workflowToDuplicate = {
			...phoneWorkflowdata[index],
			id: undefined,
			customScenario: phoneWorkflowdata[index].customScenario,
			description: 'custom-scenario',
			questions: phoneWorkflowdata[index].questions?.map((q: string) => ({ question: q })),
			previousWorkflowData,
		};

		setDuplicateWorkflow(workflowToDuplicate);
		dispatch(openModal({ modalId: MODAL_IDS.PHONE_WORKFLOW_DUPLICATE_MODAL }));
	};

	const handleDuplicateModalClose = () => {
		setDuplicateWorkflow(null);
		dispatch(closeModal());
	};

	const handleDeleteScenario = async (scenarioIndex: number, questionIndex?: number) => {
		try {
			let previousWorkflowData = [];

			if (beginningCallWorkflowQuestions && beginningCallWorkflowQuestions.length > 0) {
				previousWorkflowData.push({
					customScenario: '',
					description: 'beginning',
					questions: beginningCallWorkflowQuestions,
				});
			}

			previousWorkflowData = [...previousWorkflowData, ...phoneWorkflowdata];
			let formBuilders = previousWorkflowData;
			let scenarioDeleted = false;
			if (questionIndex !== undefined && scenarioIndex !== undefined) {
				const scenario = formBuilders.find((_, index) => index === scenarioIndex);

				if (scenario && scenario.questions) {
					const remainingQuestions = scenario.questions.filter(
						(_, qIndex) => qIndex !== questionIndex,
					);

					if (remainingQuestions.length > 0) {
						scenario.questions = remainingQuestions;
					} else {
						scenarioDeleted = true;
						scenario.questions = [];
					}
				}

				formBuilders[scenarioIndex] = scenario;
			} else if (!questionIndex) {
				formBuilders.splice(scenarioIndex, 1);
				dispatch(decrementScenarioCount(WorkflowType.INTAKE));
			}

			formBuilders = formBuilders.map((scenario) => ({
				...scenario,
				description: scenario.customScenario === '' ? 'beginning' : 'custom-scenario',
			}));

			await mutation.mutateAsync({
				formBuilders,
				questionIndex,
				scenarioIndex,
				agentId: selectedAgentId as string,
			});

			if (!scenarioDeleted && questionIndex !== undefined) {
				notification.success({ message: `Question ${questionIndex + 1} deleted successfully.` });
			} else {
				notification.success({ message: `Scenario ${scenarioIndex + 1} deleted successfully.` });
			}

			refetch();
			await fetchAndUpdateWorkflowAnalysis(dispatch, selectedAgentId || '');
		} catch (err) {
			console.error(err);
			notification.error({ message: 'Something went wrong, please try again later!' });
		}
	};

	const handleDuplicateButtonClick = (index: number) => {
		if (!canAddScenario) {
			notification.error({
				message: 'Scenario limit reached',
				description: 'Max scenarios (100) reached. Please Delete existing to add more.',
			});
			return;
		}
		handleDuplicateScenario(index);
	};
	const handleSetWorkflowAvailability = async (
		availData: TWorkflowAvailabilities,
		index: number,
		fallbackMessage: string,
		configEnabled: boolean,
	) => {
		try {
			const previousWorkflowData = [];

			if (beginningCallWorkflowQuestions && beginningCallWorkflowQuestions.length > 0) {
				previousWorkflowData.push({
					customScenario: '',
					description: 'beginning',
					questions: beginningCallWorkflowQuestions,
					boc_intake_post_execution_prompt: data?.boc_intake_post_execution_prompt || '',
				});
			}

			const updatedPhoneWorkflows = [...phoneWorkflowdata];

			const targetIndex = index - 1;

			if (updatedPhoneWorkflows[targetIndex]) {
				updatedPhoneWorkflows[targetIndex] = {
					...updatedPhoneWorkflows[targetIndex],
					availability: availData,
					configEnabled,
					fallbackMessage,
				};
			} else {
				notification.error({
					message: 'Error updating workflow: Invalid index.',
				});
				return;
			}

			const beginningScenario =
				beginningCallWorkflowQuestions?.length > 0
					? [
							{
								customScenario: '',
								description: 'beginning',
								questions: beginningCallWorkflowQuestions,
							},
						]
					: [];

			const customScenarios = updatedPhoneWorkflows.map((workflow) => ({
				...workflow,
				description: 'custom-scenario',
			}));

			const formBuilders = [...beginningScenario, ...customScenarios];
			if (!selectedAgentId) {
				throw new Error('Agent ID is required');
			}
			await mutation.mutateAsync({
				formBuilders,
				agentId: selectedAgentId as string,
			});
			notification.success({
				key: 'form-success',
				message: 'Workflow Availability has been updated!',
				duration: 3,
			});
			refetch();
		} catch (error: any) {
			console.error('Error in setWorkflowAvailability:', error);
			notification.error({
				message: 'Error updating availability',
				description: error.toString(),
			});
		}
	};

	const InfoComponent = () => {
		const handleAvailability = (index: number) => {
			const actualIndex = index - 1;
			setScenarioIndex(index);
			const val = phoneWorkflowdata[actualIndex];
			setIntakeWorkflow(val);
			dispatch(openModal({ modalId: MODAL_IDS.PHONE_WORKFLOW_AVAILABILITY_MODAL }));
		};

		const handleConfirmDelete = (index: number) => {
			ConfirmDeleteModal({
				itemName: `Scenario ${index + 1}`,
				onConfirm: () => handleDeleteScenario(index),
			});
		};

		return (
			<>
				{beginningCallWorkflowQuestions && beginningCallWorkflowQuestions.length > 0 && (
					<Spin
						spinning={
							mutation.isPending &&
							mutation.variables.scenarioIndex === 0 &&
							mutation.variables.questionIndex === undefined
						}
					>
						<Card
							title={
								<div className={`${baseClass}_button-container`}>
									<span className="font-bold text-base">Beginning of call</span>
									<div className="flex items-center gap-2">
										<Button
											type="primary"
											danger
											icon={<DeleteOutline />}
											loading={
												mutation.isPending &&
												mutation.variables.scenarioIndex === 0 &&
												mutation.variables.questionIndex === undefined
											}
											onClick={() => handleConfirmDelete(0)}
										>
											<div>Delete</div>
										</Button>
										<Button
											type="primary"
											icon={<EditOutlined />}
											onClick={() =>
												handleEditScenario(
													{
														customScenario: 'Beginning of call',
														questions: beginningCallWorkflowQuestions,
														final_collection_phrase: data?.beginning_question_final_phrase,
														postExecutionPrompt: data?.postExecutionPrompt,
													},
													0,
												)
											}
										>
											<div>Edit</div>
										</Button>
									</div>
								</div>
							}
						>
							<>
								<div className="border-b">
									<LabelWithDescription
										customClassName="mt-2"
										label="Scenario Description"
										description="Describe the scenario in which you would like to ask the caller some questions."
									/>
									<span className="pl-4 w-8/12 mt-2 pt-2 pb-4">Beginning of call</span>
								</div>

								{data.boc_intake_post_execution_prompt && (
									<div className="border-b">
										<LabelWithDescription
											customClassName="mt-2"
											label="What should the AI do next?"
											description="Specify instructions for the AI to follow after it completes the beginning of call intake."
										/>
										<span className="pl-4 w-8/12 mt-2 pt-2 pb-4">
											{data.boc_intake_post_execution_prompt ||
												'No post-execution prompt specified.'}
										</span>
									</div>
								)}

								<div className="border-b">
									<List
										className="mt-2 mb-2"
										header={<strong>Questions</strong>}
										dataSource={beginningCallWorkflowQuestions}
										renderItem={(item, qIndex) => {
											return (
												<>
													<List.Item className="!flex pl-1 text-left border hover:border-blue-100 items-start">
														{' '}
														<div className="ml-2 flex-grow text-left">{item}</div>
														<Button
															loading={
																mutation.isPending &&
																mutation.variables.questionIndex === qIndex &&
																mutation.variables.scenarioIndex === 0
															}
															onClick={() => handleDeleteScenario(0, qIndex)}
															type="primary"
															danger
															icon={<DeleteOutline />}
															className="mr-2"
														/>
													</List.Item>
												</>
											);
										}}
									/>
								</div>
							</>
						</Card>
					</Spin>
				)}

				{/* Other Cards: Maps over PhoneWorkflowdata */}
				{phoneWorkflowdata.map((phoneWorkflow, idx) => (
					<Spin
						key={idx + 1}
						spinning={
							mutation.isPending &&
							mutation.variables.scenarioIndex ===
								(beginningCallWorkflowQuestions && beginningCallWorkflowQuestions.length > 0
									? idx + 1
									: idx) &&
							mutation.variables.questionIndex === undefined
						}
					>
						<Card
							className="mt-4"
							title={
								<div className={`${baseClass}_button-container`}>
									<span className="font-bold text-base">{`Custom Scenario ${
										beginningCallWorkflowQuestions && beginningCallWorkflowQuestions.length > 0
											? idx + 2
											: idx + 1
									}`}</span>
									<div className="flex items-center gap-2">
										<Tooltip title="Duplicate This Scenario">
											<Button
												type="default"
												icon={<DifferenceOutlined />}
												onClick={() => handleDuplicateButtonClick(idx)}
												data-feature-gate-id="duplicate-intake-form-workflow-btn"
											/>
										</Tooltip>
										<Button
											type="primary"
											danger
											icon={<DeleteOutline />}
											loading={
												mutation.isPending &&
												mutation.variables.scenarioIndex ===
													(beginningCallWorkflowQuestions &&
													beginningCallWorkflowQuestions.length > 0
														? idx + 1
														: idx) &&
												mutation.variables.questionIndex === undefined
											}
											onClick={() =>
												handleConfirmDelete(
													beginningCallWorkflowQuestions &&
														beginningCallWorkflowQuestions.length > 0
														? idx + 1
														: idx,
												)
											}
										>
											<div>Delete</div>
										</Button>
										<Button
											type="primary"
											icon={<EditOutlined />}
											onClick={() =>
												handleEditScenario(
													phoneWorkflow,
													beginningCallWorkflowQuestions &&
														beginningCallWorkflowQuestions.length > 0
														? idx + 1
														: idx,
												)
											}
										>
											<div>Edit</div>
										</Button>
										{isWorkflowAvailabilityFeatureEnabled && (
											<Button
												type="primary"
												icon={<SettingOutlined />}
												onClick={() => handleAvailability(idx + 1)}
											>
												<div>Availability</div>
											</Button>
										)}
									</div>
								</div>
							}
						>
							<>
								<div className="border-b">
									<LabelWithDescription
										customClassName="mt-2"
										label="Scenario Description"
										description={
											'Describe the scenario in which you would like to ask the caller some questions.'
										}
									/>
									<span className="pl-4 w-8/12 mt-2 pt-2 pb-4">{phoneWorkflow.customScenario}</span>
									<div>
										<List
											className="mt-2 mb-2"
											header={<strong>Questions</strong>}
											dataSource={phoneWorkflow.questions}
											renderItem={(question: string, qIndex: number) => (
												<List.Item className="!flex pl-1 text-left border hover:border-blue-100 items-start">
													{' '}
													<div className="ml-2 flex-grow text-left">{question}</div>
													<Button
														loading={
															mutation.isPending &&
															mutation.variables.questionIndex === qIndex &&
															mutation.variables.scenarioIndex ===
																(beginningCallWorkflowQuestions &&
																beginningCallWorkflowQuestions.length > 0
																	? idx + 1
																	: idx)
														}
														onClick={() =>
															handleDeleteScenario(
																beginningCallWorkflowQuestions &&
																	beginningCallWorkflowQuestions.length > 0
																	? idx + 1
																	: idx,
																qIndex,
															)
														}
														type="primary"
														danger
														icon={<DeleteOutline />}
														className="mr-2"
													/>
												</List.Item>
											)}
										/>
									</div>
								</div>

								{phoneWorkflow.postExecutionPrompt && (
									<div>
										<LabelWithDescription
											customClassName="mt-2"
											label="What should the AI do next?"
										/>
										<span className="pl-4 w-8/12 mt-2 pt-2 pb-4">
											{phoneWorkflow.postExecutionPrompt}
										</span>
									</div>
								)}
							</>
						</Card>
					</Spin>
				))}
			</>
		);
	};
	const baseClass = 'phone-form';

	return (
		<div className={baseClass}>
			<OverViewCardInfo
				width={'50%'}
				formActionType="Add"
				form={
					<PhoneWorkflowForm
						mutation={mutation}
						initialValues={formInitialValues}
						refetch={refetch}
						actionType={FormActionType.ADD}
					/>
				}
				formTitle={<span className="font-bold">Add New Scenerio</span>}
				info={<InfoComponent />}
				footer={null}
				modalId={MODAL_IDS.PHONE_WORKFLOW_MODAL}
				tutorialId={TUTORIALS.INTAKE_FORM_WORKFLOW}
				displayTutorialActionBtn={!debrand}
				addButtonFeatureGateId={'add-intake-form-workflow-btn'}
			/>
			<CustomModal
				width={'50%'}
				key={MODAL_IDS.PHONE_WORKFLOW_EDIT_MODAL}
				title={<span className="font-bold">Edit Scenerio</span>}
				children={
					<PhoneWorkflowForm
						mutation={mutation}
						showAddFieldButton={false}
						initialValues={formInitialValues}
						refetch={refetch}
						actionType={FormActionType.EDIT}
						scenarioIndex={scenarioIndex}
					/>
				}
				modalId={MODAL_IDS.PHONE_WORKFLOW_EDIT_MODAL}
				footer={null}
			/>
			<CustomModal
				width={'50%'}
				title={<span className="font-bold">Duplicate Scenario</span>}
				children={
					<PhoneWorkflowForm
						mutation={mutation}
						initialValues={duplicateWorkflow ? { scenarios: [duplicateWorkflow] } : undefined}
						refetch={refetch}
						actionType={FormActionType.ADD}
						isDuplicate={true}
						showAddFieldButton={false}
					/>
				}
				modalId={MODAL_IDS.PHONE_WORKFLOW_DUPLICATE_MODAL}
				footer={null}
				cancelAction={handleDuplicateModalClose}
				destroyOnClose={true}
			/>
			{isWorkflowAvailabilityFeatureEnabled && (
				<CustomModal
					destroyOnClose={true}
					width={'50%'}
					title={
						<span className="font-bold">
							Setup WorkFlow Availability
							<Tooltip
								title="Set your AI workflow's operating hours. Choose business hours, weekends, or customize it to match your ideal schedule."
								className="mx-1"
							>
								<InfoCircleOutlined
									style={{ fontSize: '10px' }}
									className="text-gray-400 cursor-help"
								/>
							</Tooltip>
						</span>
					}
					children={
						<Spin
							spinning={mutation.isPending}
							indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
						>
							<SetupWorkflowAvailability
								mutation={handleSetWorkflowAvailability}
								workflowData={intakeWorkflow}
								scenarioIndex={scenarioIndex}
							/>
						</Spin>
					}
					modalId={MODAL_IDS.PHONE_WORKFLOW_AVAILABILITY_MODAL}
					footer={null}
				/>
			)}
		</div>
	);
};
